import { Button, Card, Col, Row } from "reactstrap";
import { faUser, faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CrewInformationUb } from "../gql/graphql";
import { CyCrewSelector } from "./CyCrewSelector";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

interface Props {
  crewInformation: ReadonlyArray<CrewInformationUb>;
  targetCrewIdentifier: string | undefined;
  setTargetCrewIdentifier: (i: string | undefined) => void;

  size: "small" | "large";
}

export function CrewSelector(props: Props) {
  const { t } = useTranslation();

  const bucketGroups = useMemo(() => {
    const groupedByBucket = new Map<string, Array<CrewInformationUb>>();

    for (const crew of props.crewInformation) {
      const crews = groupedByBucket.get(crew.bucket) ?? [];
      crews.push(crew);
      groupedByBucket.set(crew.bucket, crews);
    }

    return Array.from(groupedByBucket.entries())
      .sort(([bucketA], [bucketB]) => bucketA.localeCompare(bucketB))
      .map(([bucket, crews]) => ({
        bucket,
        crews: [...crews].sort((crewA, crewB) =>
          crewA.title.localeCompare(crewB.title),
        ),
      }));
  }, [props.crewInformation]);

  function renderLarge(
    crewName: string,
    identifier: string | undefined,
  ): JSX.Element {
    return (
      <Col key={identifier ?? "private"}>
        <Card className="text-center">
          <Button
            color={color(identifier)}
            onClick={onClick(identifier)}
            data-cy={CyCrewSelector.ButtonSelectCrew}
          >
            <div>
              <FontAwesomeIcon icon={icon(identifier)} />
            </div>
            {crewName}
          </Button>
        </Card>
      </Col>
    );
  }

  function icon(identifier: string | undefined) {
    return identifier === undefined ? faUser : faUsers;
  }

  function onClick(identifier: string | undefined) {
    return () => props.setTargetCrewIdentifier(identifier);
  }

  function color(identifier: string | undefined) {
    return identifier === props.targetCrewIdentifier ? "primary" : "light";
  }

  function renderSmall(
    crewName: string,
    identifier: string | undefined,
  ): JSX.Element {
    return (
      <Row key={identifier ?? "private"} className="mt-2">
        <Col className="d-flex">
          <Button
            color={color(identifier)}
            onClick={onClick(identifier)}
            style={{ flex: "1 1 auto" }}
            data-cy={CyCrewSelector.ButtonSelectCrew}
          >
            <FontAwesomeIcon icon={icon(identifier)} className="me-2" />
            {crewName}
          </Button>
        </Col>
      </Row>
    );
  }

  return props.size === "large" ? (
    <Row className="g-3" data-cy={CyCrewSelector.LargeSelectors}>
      {renderLarge(t("crew.dialog.private"), undefined)}
      {bucketGroups.map((group) => (
        <Col key={`bucket-${group.bucket}`} xs={12}>
          <h5 className="mt-2 mb-0">{group.bucket}</h5>
          <Row className="g-3 mt-0">
            {group.crews.map((crew) =>
              renderLarge(crew.title, crew.identifier),
            )}
          </Row>
        </Col>
      ))}
    </Row>
  ) : (
    <>
      {renderSmall(t("crew.dialog.private"), undefined)}
      {bucketGroups.map((group) => (
        <div key={`bucket-${group.bucket}`}>
          <h5 className="mt-3 mb-0">{group.bucket}</h5>
          {group.crews.map((crew) => renderSmall(crew.title, crew.identifier))}
        </div>
      ))}
    </>
  );
}
